import type { ReactNode } from "react";

import { Stack, Typography } from "@mui/material";
import { InfoIconTooltip } from "components/InfoIconTooltip";

/**
 * Consistent subsection header for content within a stepper step (e.g. "Generator
 * Status", "Waste Streams"). Renders flush-left with the step content (no
 * card/indent) so embedded sections read as children of the step rather than
 * nested cards. Uses the same `subtitle2` / 600-weight type as the facility
 * profile's section headers so subsections look identical across stepper steps.
 *
 * `action` renders right-aligned on the same row (e.g. a date-range caption); it
 * wraps below the title on narrow widths. `titleAdornment` instead sits flush
 * against the title text, for affordances that read as part of the heading.
 */
export function StepSubHeader({
  title,
  infoTooltip,
  titleAdornment,
  action,
}: {
  title: string;
  infoTooltip?: ReactNode;
  titleAdornment?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      gap={2}
      justifyContent="space-between"
      alignItems="center"
      flexWrap="wrap"
    >
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Typography variant="subtitle2" fontWeight={600}>
          {title}
        </Typography>
        {infoTooltip && <InfoIconTooltip tooltip={infoTooltip} />}
        {titleAdornment}
      </Stack>
      {action}
    </Stack>
  );
}
